/**
 * Full-screen insurance-card viewer (#327).
 *
 * The Extraction pane asks staff to confirm a member ID against a photo capped
 * at 240px — a card at postage-stamp size, where `W1` and `WI` are the same
 * three pixels. A verification screen that is too hard to verify on trains
 * staff to approve by default. This viewer is the fix: the card fills the
 * screen, zooms past native resolution (scroll, pinch, buttons), pans, rotates
 * in 90° steps — a VIEW state, like the pane's rotate; the stored image is
 * never touched — and keeps the extracted fields beside the image so a
 * digit-by-digit comparison never relies on memory.
 *
 * Modal by contract: focus is trapped while open and returns to the thumbnail
 * that opened it on close; Escape and the ✕ both exit. The Escape listener
 * runs in the CAPTURE phase and stops propagation, because the slideout
 * underneath listens for Escape on the document too — one press must close
 * the viewer, not the whole drawer.
 *
 * Both entry points — the Extraction pane's card frames and the conversation
 * thread's photos — mount this same component (the ticket's AC). It renders
 * only images already fetched through the audited card callables; no new
 * backend surface.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { mediaKey, fmtMsgTime, type CaseCardImage } from '../utils/smsThread'
import { cardFieldLabel, type CardFieldRead } from '../utils/cardReadState'
import type { CardDetailRow } from '../utils/cardDetailRows'
import type { CorrectableField } from '../utils/checkCorrection'
import type { CardCorrection } from '../hooks/useCardCorrection'
import { IconSparkle } from './Icons'
import DateInput from './DateInput'
import PayerSearch from './PayerSearch'
import {
  VIEW_RESET, ZOOM_STEP, type Size, type ViewTransform,
  fitScale, zoomBounds, nativeZoom, focalZoom,
} from '../utils/imageViewerMath'

interface CardImageViewerProps {
  /** Every image on the case, in the order the entry point listed them. */
  images: CaseCardImage[]
  /** Which image was clicked. */
  initialIndex: number
  /** The pane's extracted-field rows, shown beside the image — null/empty
   *  when the caller has no extraction in scope (viewer is image-only). */
  fields?: CardDetailRow[] | null
  /**
   * The media keys `fields` actually describe. Omit when they describe every
   * image in the list — the Extraction pane, whose list is already case-scoped.
   *
   * The conversation thread is NOT case-scoped: one phone can carry several
   * concurrent cases (siblings, or one patient's medical and vision), and it
   * renders every photo on the thread. Pairing those with the open case's rows
   * would put one patient's member ID beside another's card, under a caption
   * inviting a digit-by-digit comparison. Keys the fields don't cover render
   * the photo alone, with a line saying why.
   */
  fieldsMediaKeys?: Set<string> | null
  /**
   * The SHARED correction controller (#349) — the same one the Extraction pane
   * holds, not a second draft. Present ⇒ the rows beside the enlarged card are
   * editable, so a digit read wrong at 100% is fixed without the card leaving
   * the screen. Absent ⇒ the panel stays the read-only list (the staff console,
   * the conversation thread, a view-mode session).
   */
  correction?: CardCorrection | null
  onClose: () => void
}

const dist = (a: { x: number; y: number }, b: { x: number; y: number }) =>
  Math.hypot(a.x - b.x, a.y - b.y)

/** Labels for the two inputs a Cardholder row splits into. */
const NAME_LABEL: Partial<Record<CorrectableField, string>> = {
  firstName: 'First name',
  lastName: 'Last name',
}

/**
 * One editable row beside the enlarged card (#349).
 *
 * The row's VALUE is what the check ASKED the payer, so "edited" means "differs
 * from the question we are putting". Where the check holds nothing the card read
 * stands in, and a field neither side has opens empty with the read's own
 * failure as its placeholder — a card we couldn't read must never arrive
 * pre-filled with a value it never showed. The placeholder is `cardFieldLabel`,
 * the same sentence the pane prints, so the two surfaces cannot word it
 * differently.
 */
function CardEditRow(
  { row, correction }: { row: CardDetailRow; correction: CardCorrection },
) {
  const { values, setValue, setPayer, isEdited, originalOf, saving, canWrite, ready } = correction
  const edited = row.fields.some(isEdited)
  const placeholder = row.state.kind === 'read' ? undefined : cardFieldLabel(row.state)
  // What the check asked before the edit, for the "was …" title the ticket puts
  // on the row (the hover-only affordance the PO accepted — clinics are on
  // desktop).
  const was = row.fields.map(originalOf).filter(Boolean).join(' ')
  // `!ready` for the same reason the pane's rows show no pencil then: a
  // keystroke before the host has settled which check these are would freeze
  // the wrong values into the draft (see CardDetailRows).
  const disabled = !canWrite || !ready || saving
  const inputClass = (extra = '') =>
    `rl-viewer__edit-input${extra}${edited ? ' rl-viewer__edit-input--edited' : ''}`
  const id = `rl-viewer-edit-${row.fields.join('-')}`

  return (
    <div className={`rl-viewer__edit-row${row.state.kind === 'not_on_card' ? ' rl-viewer__edit-row--absent' : ''}`}>
      <label className="rl-viewer__edit-label" htmlFor={id} title={was ? `Was ${was}` : undefined}>
        {edited && <span className="rl-viewer__edit-dot" aria-hidden="true" />}
        <span>{row.label}</span>
        {edited && <span className="rl-viewer__sr-only">, edited</span>}
        {/* Both marks describe the CARD's value; an edited row shows the
            operator's text instead, so neither may ride along with it. */}
        {row.ai && row.state.kind === 'read' && !edited && (
          <span
            className="rl-viewer__edit-ai"
            title="The card names a different insurer than the one on file, so the re-check runs with the card's payer and member ID."
          >
            <IconSparkle />
          </span>
        )}
        {row.stated && row.state.kind === 'read' && !edited && (
          <span
            className="rl-viewer__edit-stated"
            title="The patient sent this in a text message — it was not read off the card."
          >
            from a text
          </span>
        )}
      </label>

      {row.fields[0] === 'payerName' ? (
        <PayerSearch
          inputId={id}
          value={values.payerName}
          disabled={disabled}
          hideLabel
          dark
          placeholder={placeholder ?? 'Search payers…'}
          inputClassName={inputClass()}
          // No `onClear`, same as the pane's row: a pick survives typing that
          // never became a pick (see CardDetailRows).
          onSelect={(p) => setPayer(p)}
        />
      ) : row.fields[0] === 'dateOfBirth' ? (
        <DateInput
          id={id}
          className={inputClass()}
          value={values.dateOfBirth}
          disabled={disabled}
          onValueChange={(v) => setValue('dateOfBirth', v)}
        />
      ) : row.fields.length > 1 ? (
        // One row, two inputs: the card prints a single name line and #309's
        // read state is defined over the pair, but a correction has to land on
        // first and last separately.
        <div className="rl-viewer__edit-pair">
          {row.fields.map((f, i) => (
            <input
              key={f}
              id={i === 0 ? id : undefined}
              className={inputClass()}
              value={values[f]}
              disabled={disabled}
              placeholder={placeholder ?? NAME_LABEL[f]}
              aria-label={NAME_LABEL[f]}
              onChange={(e) => setValue(f, e.target.value)}
            />
          ))}
        </div>
      ) : (
        <input
          id={id}
          className={inputClass(row.fields[0] === 'memberId' ? ' rl-viewer__edit-input--mono' : '')}
          value={values[row.fields[0]]}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => setValue(row.fields[0], e.target.value)}
        />
      )}
    </div>
  )
}

/** The same three-way split the pane uses (#309/#349): an unread value is a
 *  gap (grey), and only printed-but-illegible earns the darker italic. The two
 *  surfaces render from one row builder and must not dress it differently. */
function viewerValueModifier(state: CardFieldRead): string {
  switch (state.kind) {
    case 'read': return ''
    case 'unreadable': return ' rl-viewer__field-value--illegible'
    default: return ' rl-viewer__field-value--absent'
  }
}

/** Typing happens in these; the viewer's single-key shortcuts must not. */
function isTextEntry(target: EventTarget | null): boolean {
  return target instanceof HTMLElement
    && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
}

/** Everything the Tab trap must cycle. Buttons alone was enough until the panel
 *  grew inputs — trapping Tab while excluding them makes the fields
 *  unreachable from the keyboard, which is the viewer's own AC. */
const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [href], select, textarea'

export default function CardImageViewer({
  images, initialIndex, fields, fieldsMediaKeys, correction, onClose,
}: CardImageViewerProps) {
  const count = images.length
  // The image being viewed is tracked by KEY, not by list position: the list
  // is live (thread photos load one by one, `pickCaseCardMedia` orders newest
  // first, and an agent-turn attribution can move a photo to another case), so
  // an index would silently land on a DIFFERENT photo when the list shifts
  // under an open viewer — the zoomed member ID swapping mid-comparison.
  const [currentKey, setCurrentKey] = useState<string>(() => {
    const first = images[Math.min(Math.max(initialIndex, 0), count - 1)]
    return first ? mediaKey(first.intakeId, first.objectIndex) : ''
  })
  const [view, setView] = useState<ViewTransform>(VIEW_RESET)
  const [deg, setDeg] = useState(0)
  const [natural, setNatural] = useState<Size | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [stageSize, setStageSize] = useState<Size | null>(null)
  const [panning, setPanning] = useState(false)

  const dialogRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  useEffect(() => { onCloseRef.current = onClose }, [onClose])

  // Closing discards NOTHING (#349): the draft lives in the host's controller
  // (the Recovery drawer) and is still on the pane's rows when this unmounts,
  // so Escape and the ✕ close without a warning — the drawer's own #274 guard
  // is the one that asks, on the close that would actually lose the edit.
  // Browser Back is owned by the pane that mounts the viewer: a sentinel in
  // here would strand its history slot on every in-app close (#231), because
  // the viewer unmounts on close and only a hook that outlives the close can
  // reclaim the slot.

  // Derived position: follows the key wherever the list puts it; only if the
  // key left the list entirely does the viewer fall back to the first image.
  const found = images.findIndex((i) => mediaKey(i.intakeId, i.objectIndex) === currentKey)
  const index = found >= 0 ? found : 0
  const image = images[index] as CaseCardImage | undefined

  // Load state belongs to the PHOTO on screen, not to the navigation that
  // changed it. `step()` reset it, but the derived-index fallback above —
  // the tracked key leaving a LIVE list — never goes through step(), so a
  // failed photo left `loadFailed` set for whatever mounted next. That is
  // unrecoverable rather than cosmetic: the <img> is not rendered while
  // `loadFailed`, so the element whose onLoad would clear it never mounts,
  // and a perfectly good photo reads as broken for the life of the viewer.
  // Stale `natural` sizes the new photo by the old one's dimensions.
  // Adjusted during render (this component's OWN state, never a parent's)
  // so the replacement never paints a frame at the previous scale.
  const shownKey = image ? mediaKey(image.intakeId, image.objectIndex) : ''
  const [loadedKey, setLoadedKey] = useState(shownKey)
  if (loadedKey !== shownKey) {
    setLoadedKey(shownKey)
    setNatural(null)
    setLoadFailed(false)
  }
  const fit = natural && stageSize ? fitScale(stageSize, natural, deg) : 1
  const bounds = useMemo(() => zoomBounds(fit), [fit])
  // The handlers close over stale fit/bounds otherwise — wheel/pinch read the ref.
  const boundsRef = useRef(bounds)
  useEffect(() => { boundsRef.current = bounds }, [bounds])
  const displayScale = fit * view.zoom

  /** Switching images resets the view — zoom framing is per photo. */
  const indexRef = useRef(index)
  useEffect(() => { indexRef.current = index }, [index])
  const imagesRef = useRef(images)
  useEffect(() => { imagesRef.current = images }, [images])
  const step = useCallback((d: number) => {
    const list = imagesRef.current
    if (list.length < 2) return
    const next = list[(indexRef.current + d + list.length) % list.length]
    setCurrentKey(mediaKey(next.intakeId, next.objectIndex))
    setView(VIEW_RESET)
    setDeg(0)
  }, [])

  const zoomBy = useCallback((factor: number) => {
    setView((v) => focalZoom(v, v.zoom * factor, boundsRef.current, 0, 0))
  }, [])
  /** Jump to an absolute zoom — what the 100% button needs; the ×1.5 ladder
   *  steps straight past native resolution (91% → 136% → 205% on a typical
   *  card), so the one scale staff are asked to read a member ID at was the
   *  one scale no sequence of clicks could land on. */
  const zoomTo = useCallback((zoom: number) => {
    setView((v) => focalZoom(v, zoom, boundsRef.current, 0, 0))
  }, [])
  const reset = useCallback(() => setView(VIEW_RESET), [])
  /** Rotate is view-only (#327 AC) — nothing stored changes. Pan resets
   *  because the old offset points at the wrong part of a rotated card. */
  const rotate = useCallback((d: number) => {
    setDeg((x) => (x + d + 360) % 360)
    setView((v) => ({ ...v, tx: 0, ty: 0 }))
  }, [])

  // Focus in, focus back out. The opener (a thumbnail button) is the active
  // element at mount; closing hands focus back to it — the AC's "focus
  // returns to the card that was clicked".
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    dialogRef.current?.focus()
    return () => { opener?.focus() }
  }, [])

  // One keyboard surface for the whole viewer, capture-phase so the slideout's
  // own document-level Escape handler never sees the press. Tab is trapped
  // manually — the viewer is modal, and the drawer's buttons are still in the
  // DOM underneath it.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault(); e.stopPropagation()
        onCloseRef.current()
        return
      }
      // Every shortcut below is a BARE character, and the panel is now full of
      // inputs: unguarded, typing a member id containing "-" or "0" zoomed the
      // card instead of entering the digit, and an arrow key inside a field
      // walked to the next photo. Editing wins inside a field; Escape does not,
      // because leaving is the one thing that must work from anywhere.
      if (isTextEntry(e.target)) {
        if (e.key !== 'Tab') return
      }
      if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); return }
      if (e.key === 'ArrowRight') { e.preventDefault(); step(1); return }
      if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomBy(ZOOM_STEP); return }
      if (e.key === '-') { e.preventDefault(); zoomBy(1 / ZOOM_STEP); return }
      if (e.key === '0') { e.preventDefault(); reset(); return }
      if (e.key === 'Tab') {
        const dialog = dialogRef.current
        if (!dialog) return
        const els = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE))
        e.preventDefault()
        if (els.length === 0) { dialog.focus(); return }
        const i = els.indexOf(document.activeElement as HTMLElement)
        const next = e.shiftKey
          ? (i <= 0 ? els.length - 1 : i - 1)
          : (i === -1 || i === els.length - 1 ? 0 : i + 1)
        els[next].focus()
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [step, zoomBy, reset])

  // Stage size, live — the fit scale depends on it. ResizeObserver is absent
  // under jsdom; the fallback single measure is enough there.
  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return
    const measure = () => setStageSize({ w: stage.clientWidth, h: stage.clientHeight })
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(stage)
    return () => ro.disconnect()
  }, [])

  // Scroll-to-zoom at the cursor. Native listener because React registers
  // wheel passively — preventDefault (needed so the drawer doesn't scroll
  // behind the viewer) is a no-op from the synthetic handler.
  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const rect = stage.getBoundingClientRect()
      const px = e.clientX - (rect.left + rect.width / 2)
      const py = e.clientY - (rect.top + rect.height / 2)
      const factor = Math.exp(-e.deltaY * 0.002)
      setView((v) => focalZoom(v, v.zoom * factor, boundsRef.current, px, py))
    }
    stage.addEventListener('wheel', onWheel, { passive: false })
    return () => stage.removeEventListener('wheel', onWheel)
  }, [])

  // Pointer pan + two-finger pinch. `touch-action: none` on the stage keeps
  // the browser's own gestures out of the way, so no preventDefault dance.
  const pointers = useRef(new Map<number, { x: number; y: number }>())
  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    // The controls and nav arrows live inside the stage: capturing THEIR
    // pointer would redirect the pointerup to the stage and swallow the
    // button's click (found driving the real app — jsdom's no-op
    // setPointerCapture never showed it). Pan starts on the image/backdrop only.
    if (e.target instanceof Element && e.target.closest('button')) return
    stageRef.current?.setPointerCapture?.(e.pointerId)
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pointers.current.size === 1) setPanning(true)
  }, [])
  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const prev = pointers.current.get(e.pointerId)
    if (!prev) return
    const now = { x: e.clientX, y: e.clientY }
    if (pointers.current.size === 1) {
      // Pan only while zoomed in — at fit there is nowhere to pan to.
      setView((v) => v.zoom > 1
        ? { ...v, tx: v.tx + (now.x - prev.x), ty: v.ty + (now.y - prev.y) }
        : v)
    } else if (pointers.current.size === 2) {
      const stage = stageRef.current
      const other = Array.from(pointers.current.entries())
        .find(([id]) => id !== e.pointerId)?.[1]
      if (stage && other) {
        const rect = stage.getBoundingClientRect()
        const cx = rect.left + rect.width / 2
        const cy = rect.top + rect.height / 2
        const oldMid = { x: (prev.x + other.x) / 2 - cx, y: (prev.y + other.y) / 2 - cy }
        const newMid = { x: (now.x + other.x) / 2 - cx, y: (now.y + other.y) / 2 - cy }
        const factor = dist(prev, other) > 0 ? dist(now, other) / dist(prev, other) : 1
        setView((v) => focalZoom(
          { ...v, tx: v.tx + (newMid.x - oldMid.x), ty: v.ty + (newMid.y - oldMid.y) },
          v.zoom * factor, boundsRef.current, newMid.x, newMid.y,
        ))
      }
    }
    pointers.current.set(e.pointerId, now)
  }, [])
  const onPointerEnd = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(e.pointerId)
    if (pointers.current.size === 0) setPanning(false)
  }, [])

  const pct = natural ? Math.round(displayScale * 100) : null
  // The 100% button's target and its already-there state. Compared on the
  // exact zoom rather than the rounded percentage, so a wheel gesture that
  // lands on 99.6% (displayed "100%") still snaps to true native on click.
  const native = nativeZoom(fit)
  const atNative = !natural || view.zoom === native
  // Do the rows describe the photo currently on screen? Tracked per image, not
  // per viewer, because the arrows walk across photos that may belong to
  // different cases on a shared thread.
  const fieldsDescribeThisImage = !fieldsMediaKeys
    || (!!image && fieldsMediaKeys.has(mediaKey(image.intakeId, image.objectIndex)))
  const hasFields = !!fields && fields.length > 0
  // Any value the patient typed rather than the card printing it — the panel
  // caption must not claim the whole panel came off the photo.
  const anyStated = hasFields && fields.some((f) => f.stated && f.state.kind === 'read')
  // Any row showing what the check ASKED rather than what the card read — see
  // the panel caption below.
  const anyAsked = hasFields && fields.some((f) => f.fromAsked)

  // Portalled to <body> deliberately. `.slideout-panel` is `position: fixed;
  // z-index: 101`, which makes it a STACKING CONTEXT: this dialog’s own
  // z-index then only ranks it among that panel’s children, and against
  // anything outside the panel the whole subtree competes as 101. The
  // impersonation banner (`.imp-banner`, z-index 900, outside it) therefore
  // painted over the viewer’s top bar, covering the title and the ✕ that
  // #327 requires as a visible close control. No z-index here can win from
  // inside that context; leaving it is the only fix that does not also lift
  // the entire drawer over the banner.
  return createPortal(
    <div
      className="rl-viewer"
      role="dialog"
      aria-modal="true"
      aria-label="Insurance card viewer"
      ref={dialogRef}
      tabIndex={-1}
    >
      <div className="rl-viewer__topbar">
        <span className="rl-viewer__title">
          {count > 1 ? `Image ${index + 1} of ${count}` : 'Card image'}
          {image?.receivedAt ? ` · received ${fmtMsgTime(image.receivedAt)}` : ''}
        </span>
        <button
          type="button"
          className="rl-viewer__btn rl-viewer__close"
          onClick={onClose}
          aria-label="Close viewer"
        >
          <svg width="16" height="16" viewBox="0 0 18 18" fill="none" aria-hidden="true">
            <path d="M4.5 4.5l9 9M13.5 4.5l-9 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <div className="rl-viewer__body">
        <div
          className={`rl-viewer__stage${panning && view.zoom > 1 ? ' rl-viewer__stage--panning' : ''}`}
          ref={stageRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
        >
          {image && !loadFailed && (
            <img
              key={mediaKey(image.intakeId, image.objectIndex)}
              className="rl-viewer__img"
              src={image.url}
              alt={`Insurance card image ${index + 1} of ${count}, enlarged`}
              draggable={false}
              onLoad={(e) => {
                const el = e.currentTarget
                setNatural({ w: el.naturalWidth, h: el.naturalHeight })
              }}
              onError={() => setLoadFailed(true)}
              style={{
                transform:
                  `translate(${view.tx}px, ${view.ty}px) rotate(${deg}deg) ` +
                  `scale(${displayScale}) translate(-50%, -50%)`,
                visibility: natural ? 'visible' : 'hidden',
              }}
            />
          )}
          {/* A decode failure must never read as an empty patient state (the
              #321 lesson this whole ticket is about) — say what happened. */}
          {loadFailed && (
            <div className="rl-viewer__load-error" role="alert">
              This image couldn&apos;t be displayed. Close the viewer and reopen the
              case — if it persists, the stored photo may be corrupted.
            </div>
          )}
          {count > 1 && (
            <>
              <button
                type="button"
                className="rl-viewer__nav rl-viewer__nav--prev"
                onClick={() => step(-1)}
                aria-label="Previous image"
              >
                ‹
              </button>
              <button
                type="button"
                className="rl-viewer__nav rl-viewer__nav--next"
                onClick={() => step(1)}
                aria-label="Next image"
              >
                ›
              </button>
            </>
          )}
          <div className="rl-viewer__controls">
            <button type="button" className="rl-viewer__btn" onClick={() => zoomBy(1 / ZOOM_STEP)} aria-label="Zoom out">−</button>
            <span className="rl-viewer__pct" aria-live="polite">
              {pct !== null ? `${pct}%` : '…'}
            </span>
            <button type="button" className="rl-viewer__btn" onClick={() => zoomBy(ZOOM_STEP)} aria-label="Zoom in">+</button>
            <button type="button" className="rl-viewer__btn" onClick={reset} disabled={view.zoom === 1}>Fit</button>
            <button
              type="button"
              className="rl-viewer__btn"
              onClick={() => zoomTo(native)}
              disabled={atNative}
              title="The photo's own pixels — the size the member ID was captured at"
            >
              100%
            </button>
            <span className="rl-viewer__sep" aria-hidden="true" />
            <button
              type="button"
              className="rl-viewer__btn"
              onClick={() => rotate(-90)}
              aria-label="Rotate left"
              title="Rotates this view only — the stored image is unchanged"
            >
              ⟲
            </button>
            <button
              type="button"
              className="rl-viewer__btn"
              onClick={() => rotate(90)}
              aria-label="Rotate right"
              title="Rotates this view only — the stored image is unchanged"
            >
              ⟳
            </button>
          </div>
          <div className="rl-viewer__hint">Scroll or pinch to zoom · drag to pan · 100% = the photo&apos;s own pixels</div>
        </div>

        {hasFields && fieldsDescribeThisImage && correction && (
          <aside className="rl-viewer__fields rl-viewer__fields--edit" aria-label="Extracted details">
            <div className="rl-viewer__fields-head">
              <span className="rl-viewer__fields-title">Extracted details</span>
              <span className="rl-viewer__fields-hint">Fix it here</span>
            </div>
            <div className="rl-viewer__edits">
              {fields!.map((row) => (
                <CardEditRow key={row.label} row={row} correction={correction} />
              ))}
            </div>
            {correction.notice && (
              <p className="rl-viewer__edit-notice" role="status">{correction.notice}</p>
            )}
            {correction.error && (
              <p className="rl-viewer__edit-error" role="alert">{correction.error}</p>
            )}
            <div className="rl-viewer__edit-actions">
              <button
                type="button"
                className="rl-viewer__edit-save"
                onClick={() => { void correction.save() }}
                disabled={!correction.canWrite || !correction.ready || !correction.dirty
                  || correction.saving || correction.saved}
              >
                {correction.saving ? 'Re-checking…' : 'Save & re-check'}
              </button>
              <button
                type="button"
                className="rl-viewer__edit-discard"
                onClick={correction.discard}
                disabled={!correction.dirty || correction.saving}
              >
                Discard
              </button>
            </div>
          </aside>
        )}

        {hasFields && fieldsDescribeThisImage && !correction && (
          <aside className="rl-viewer__fields" aria-label="Extracted details">
            <div className="rl-viewer__fields-title">Extracted details</div>
            {fields!.map((row) => (
              <div className="rl-viewer__field" key={row.label}>
                <span className="rl-viewer__field-label">{row.label}</span>
                <span className={`rl-viewer__field-value${viewerValueModifier(row.state)}`}>
                  {cardFieldLabel(row.state)}
                </span>
                {row.ai && row.state.kind === 'read' && (
                  <span className="rl-viewer__field-ai">
                    <IconSparkle /> Suggested by VitalRev AI
                  </span>
                )}
                {/* The provenance the merged read loses: a texted answer is
                    stored with found=true and confidence 1, exactly like a
                    clean OCR hit. Staff deciding whether to trust a value need
                    to know the card never showed it. */}
                {row.stated && row.state.kind === 'read' && (
                  <span
                    className="rl-viewer__field-stated"
                    title="The patient sent this in a text message — it was not read off the card."
                  >
                    Patient provided
                  </span>
                )}
              </div>
            ))}
            <div className="rl-viewer__fields-note">
              {/* The panel has to say what these values ARE, and since #349 that
                  is no longer always the card read: a row shows what the check
                  asked wherever it has a value, so on any case that has been
                  corrected "what the AI read" describes values nobody is asking
                  the payer about any more. */}
              {anyAsked
                ? `What we're asking the payer — compare it against the card${count > 1 ? 's' : ''}.`
                : anyStated
                  ? `What we hold for this card — read from the photo${count > 1 ? 's' : ''} except where marked. Compare it against the card.`
                  : `What the AI read from the photo${count > 1 ? 's' : ''} — compare it against the card.`}
            </div>
          </aside>
        )}
        {/* A photo from another case on the same thread. Showing nothing would
            read as "no data"; this says which question the panel can't answer
            here. */}
        {hasFields && !fieldsDescribeThisImage && (
          <aside className="rl-viewer__fields" aria-label="Extracted details">
            <div className="rl-viewer__fields-title">Extracted details</div>
            <div className="rl-viewer__fields-note">
              This photo isn&apos;t the source of the open case&apos;s extracted
              details, so they aren&apos;t shown beside it — several cases can
              share one phone. Open that case to see what was read from this
              card.
            </div>
          </aside>
        )}
      </div>
    </div>,
    document.body,
  )
}
